import { ApolloLink } from "@apollo/client";
import type { FetchResult } from "@apollo/client";
import { GraphQLError } from "graphql";

import { EXPECTED_ERROR_EXTENSION, type ExpectedError } from "../errors";

type ExpectedErrorsLinkConfig = {
  /**
   * Field name on each mutation payload that carries the error array.
   * Defaults to `expectedErrors` (the migration target). Point it at
   * `userErrors` only if you need to bridge the legacy field instead.
   */
  fieldName?: string;
  debug?: boolean;
};

function isExpectedErrorEntry(value: unknown): value is ExpectedError {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as { message?: unknown }).message === "string"
  );
}

/**
 * Collects every `<fieldName>` array found on the top-level mutation payloads
 * of a response. A response usually has one root mutation field, but we scan
 * all of them to stay correct for multi-field operations.
 */
function collectExpectedErrors(data: FetchResult["data"], fieldName: string): ExpectedError[] {
  if (!data || typeof data !== "object") {
    return [];
  }

  const collected: ExpectedError[] = [];
  for (const payload of Object.values(data as Record<string, unknown>)) {
    if (!payload || typeof payload !== "object") {
      continue;
    }
    const field = (payload as Record<string, unknown>)[fieldName];
    if (Array.isArray(field)) {
      collected.push(...field.filter(isExpectedErrorEntry));
    }
  }
  return collected;
}

/**
 * Bridges backend `expectedErrors` into Apollo's top-level `errors[]` so that
 * existing `catch (ApolloError)` blocks and `getErrorMessage()` keep working
 * without any per-container refactor.
 *
 * Behavior:
 * - If a mutation payload returns a non-empty `expectedErrors` array, each
 *   entry is promoted to a `GraphQLError` on `result.errors`, carrying
 *   `code` / `traceId` / `confirmations` in `extensions`. Apollo then rejects
 *   `mutate()` with an `ApolloError`, exactly as a top-level error would.
 * - If the array is absent or empty (the state today, before BE migrates),
 *   the response is forwarded untouched — zero behavior change.
 *
 * This is the permanent Phase 1 bridge from the error-handling transition plan.
 * In Phase 3, containers read the promoted errors via the helpers in
 * `../errors` (`getExpectedErrors`, `getErrorCode`) instead of string-matching
 * `error.message`.
 *
 * NOTE: this only PROMOTES expected errors; it does not report them. Sentry
 * routing for *unexpected* errors lives in the error link (Phase 3), which uses
 * the `expectedError` extension marker to skip what the BE already reported.
 */
export function createExpectedErrorsLink(config: ExpectedErrorsLinkConfig = {}): ApolloLink {
  const fieldName = config.fieldName ?? "expectedErrors";

  return new ApolloLink((operation, forward) => {
    return forward(operation).map((result) => {
      const expectedErrors = collectExpectedErrors(result.data, fieldName);
      if (expectedErrors.length === 0) {
        return result;
      }

      // Promote every expected error. The first one drives existing
      // message-based UX (getErrorMessage reads [0]); the full set keeps all
      // codes/traceIds available for typed handling in Phase 3.
      const promoted = expectedErrors.map(
        (e) =>
          new GraphQLError(`${e.message}${e.traceId ? ` (${e.traceId})` : ""}`, {
            extensions: {
              code: e.code ?? undefined,
              traceId: e.traceId ?? undefined,
              ...(e.confirmations ? { confirmations: e.confirmations } : {}),
              [EXPECTED_ERROR_EXTENSION]: true,
            },
          }),
      );

      if (config.debug) {
        console.log(
          `[expectedErrorsLink] ${operation.operationName}: promoted ${promoted.length} error(s)`,
          promoted.map((e) => e.message),
        );
      }

      return {
        ...result,
        errors: [...(result.errors ?? []), ...promoted],
      };
    });
  });
}
